// URL hash serializer/deserializer for shareable map state.
// Format (v=1):
//   #v=1&l=t_mean&m=heatmap&z=7&c=45.8,10.5&f=alt:0-1500;tjul:max:24&p=alpine&poi=hospitals,stations&sel=4,IT-22,022205&cc=IT&q=<base64url>
//
// Hash is used (not query params) → не уходит на сервер.

import { F_DEFAULTS } from '@/features/filters/filter-fns';
import type { Filters, PresetId, Zone } from '@/features/filters/types';
import type { ChoroplethMode } from '@/features/choropleth/store';
import type { PoiCategory } from '@/features/poi/types';
import type { Selection } from '@/features/places/types';
import { EMPTY_SELECTION } from '@/features/places/types';
import { DEFAULT_COUNTRY, isCountryCode, type CountryCode } from '@/data/countries';
import { PROFILE_PRESETS_BY_ID, type ProfilePresetId } from '@/lib/scoring/profilePresets';

const DEFAULT_PROFILE: ProfilePresetId = 'balanced';

export const URL_SCHEMA_VERSION = 1;
export const MAX_SELECTION_IN_URL = 50;

export interface UrlState {
  layer?: string;
  mode?: ChoroplethMode;
  zoom?: number;
  center?: [number, number];
  filters?: Filters;
  preset?: PresetId;
  poi?: PoiCategory[];
  selection?: Selection;
  // Country code for multi-country routing. Default = 'IT' (omitted in URL).
  country?: CountryCode;
  // Opaque base64url-encoded quiz result blob. Consumed by future features/quiz/result-to-filters.
  quizResultBlob?: string;
  // F4.0 scoring preset (balanced/family/nomad/retiree/mountain). Omitted from URL
  // when default 'balanced'. Param key: 'pf' (profile fit) — 'p' is taken by filter preset.
  profile?: ProfilePresetId;
  // Set when navigating from quiz results — triggers profile banner on map. One-way flag.
  fromQuiz?: boolean;
}

const VALID_ZONES = new Set<Zone>([
  'pianura',
  'collina_interna',
  'collina_litoranea',
  'montagna_interna',
  'montagna_litoranea',
]);

function num(v: string | undefined, fallback?: number): number | undefined {
  if (v == null || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function roundTo(n: number, digits: number): number {
  const k = 10 ** digits;
  return Math.round(n * k) / k;
}

// ─── Filters encoding ─────────────────────────────────────────────────────────
// alt:0-1500;tjul:max:24;tjan:min:-3;hum:max:78;snow:min:30;sunny:min:200;zones:montagna_interna,collina_interna;pop:max:50000;hosp:max:20

function encodeFilters(f: Filters): string {
  const parts: string[] = [];
  if (f.alt_min !== F_DEFAULTS.alt_min || f.alt_max !== F_DEFAULTS.alt_max) {
    parts.push(`alt:${f.alt_min}-${f.alt_max}`);
  }
  if (f.t_jul_max !== null) parts.push(`tjul:max:${f.t_jul_max}`);
  if (f.t_jan_min !== null) parts.push(`tjan:min:${f.t_jan_min}`);
  if (f.hum_max !== null) parts.push(`hum:max:${f.hum_max}`);
  if (f.snow_min !== null) parts.push(`snow:min:${f.snow_min}`);
  if (f.sunny_min !== null) parts.push(`sunny:min:${f.sunny_min}`);
  if (f.zones.size > 0) parts.push(`zones:${Array.from(f.zones).join(',')}`);
  if (f.pop_max !== null) parts.push(`pop:max:${f.pop_max}`);
  if (f.hosp_max !== null) parts.push(`hosp:max:${f.hosp_max}`);
  return parts.join(';');
}

function decodeFilters(s: string): Filters {
  const out: Filters = {
    ...F_DEFAULTS,
    zones: new Set<Zone>(),
  };
  if (!s) return out;
  for (const chunk of s.split(';')) {
    const [key, ...rest] = chunk.split(':');
    if (!key) continue;
    switch (key) {
      case 'alt': {
        const m = rest.join(':').match(/^(-?\d+)-(-?\d+)$/);
        if (m) {
          out.alt_min = Number(m[1]);
          out.alt_max = Number(m[2]);
        }
        break;
      }
      case 'tjul':
        if (rest[0] === 'max') out.t_jul_max = num(rest[1]) ?? null;
        break;
      case 'tjan':
        if (rest[0] === 'min') out.t_jan_min = num(rest[1]) ?? null;
        break;
      case 'hum':
        if (rest[0] === 'max') out.hum_max = num(rest[1]) ?? null;
        break;
      case 'snow':
        if (rest[0] === 'min') out.snow_min = num(rest[1]) ?? null;
        break;
      case 'sunny':
        if (rest[0] === 'min') out.sunny_min = num(rest[1]) ?? null;
        break;
      case 'pop':
        if (rest[0] === 'max') out.pop_max = num(rest[1]) ?? null;
        break;
      case 'hosp':
        if (rest[0] === 'max') out.hosp_max = num(rest[1]) ?? null;
        break;
      case 'zones': {
        const zs = (rest.join(':') || '').split(',').filter((z) => VALID_ZONES.has(z as Zone));
        out.zones = new Set(zs as Zone[]);
        break;
      }
    }
  }
  return out;
}

// ─── Selection encoding ───────────────────────────────────────────────────────

function encodeSelection(sel: Selection): { encoded: string; truncated: boolean } {
  const all = [...sel.regions, ...sel.provinces, ...sel.comuni];
  const truncated = all.length > MAX_SELECTION_IN_URL;
  const list = truncated ? all.slice(0, MAX_SELECTION_IN_URL) : all;
  return { encoded: list.join(','), truncated };
}

function decodeSelection(s: string): Selection {
  if (!s) return EMPTY_SELECTION;
  const out: Selection = { regions: [], provinces: [], comuni: [] };
  for (const code of s.split(',')) {
    if (!code) continue;
    if (/^\d{6}$/.test(code)) out.comuni.push(code);
    else if (/^[A-Z]{2}$/.test(code)) out.provinces.push(code);
    else if (/^\d{1,2}$/.test(code)) out.regions.push(code);
  }
  return out;
}

// ─── Top-level encode / decode ────────────────────────────────────────────────

export function encodeUrlState(state: UrlState): string {
  const params = new URLSearchParams();
  params.set('v', String(URL_SCHEMA_VERSION));
  if (state.layer && state.layer !== 'altitude') params.set('l', state.layer);
  if (state.mode && state.mode !== 'choropleth') params.set('m', state.mode);
  if (state.zoom !== undefined) params.set('z', String(state.zoom));
  if (state.center) {
    params.set('c', `${roundTo(state.center[0], 4)},${roundTo(state.center[1], 4)}`);
  }
  if (state.filters) {
    const f = encodeFilters(state.filters);
    if (f) params.set('f', f);
  }
  if (state.preset) params.set('p', state.preset);
  if (state.poi && state.poi.length > 0) params.set('poi', state.poi.join(','));
  if (state.selection) {
    const total =
      state.selection.regions.length +
      state.selection.provinces.length +
      state.selection.comuni.length;
    if (total > 0) {
      const { encoded } = encodeSelection(state.selection);
      params.set('sel', encoded);
    }
  }
  if (state.country && state.country !== DEFAULT_COUNTRY) {
    params.set('cc', state.country);
  }
  if (state.quizResultBlob) {
    params.set('q', state.quizResultBlob);
  }
  if (state.profile && state.profile !== DEFAULT_PROFILE) {
    params.set('pf', state.profile);
  }
  if (state.fromQuiz) params.set('fq', '1');
  return params.toString();
}

export function decodeUrlState(hash: string): UrlState {
  // Strip leading '#' or '#/' if present
  const clean = hash.replace(/^#\/?/, '');
  if (!clean) return {};
  const params = new URLSearchParams(clean);
  const out: UrlState = {};

  const v = params.get('v');
  if (v && Number(v) !== URL_SCHEMA_VERSION) {
    // future: legacy migrations. Сейчас просто читаем best-effort.
  }

  const layer = params.get('l');
  if (layer) out.layer = layer;

  const mode = params.get('m');
  if (mode === 'choropleth' || mode === 'heatmap') out.mode = mode;

  const z = num(params.get('z') ?? undefined);
  if (z !== undefined) out.zoom = Math.max(1, Math.min(20, z));

  const c = params.get('c');
  if (c) {
    const [lat, lon] = c.split(',').map((s) => Number(s));
    if (Number.isFinite(lat) && Number.isFinite(lon)) out.center = [lat, lon];
  }

  const f = params.get('f');
  if (f) out.filters = decodeFilters(f);

  const p = params.get('p');
  if (p) out.preset = p as PresetId;

  const poi = params.get('poi');
  if (poi) out.poi = poi.split(',').filter(Boolean) as PoiCategory[];

  const sel = params.get('sel');
  if (sel) out.selection = decodeSelection(sel);

  const cc = params.get('cc');
  if (cc && isCountryCode(cc)) out.country = cc;

  const q = params.get('q');
  if (q) out.quizResultBlob = q;

  const pf = params.get('pf');
  if (pf && pf in PROFILE_PRESETS_BY_ID) out.profile = pf as ProfilePresetId;

  if (params.get('fq') === '1') out.fromQuiz = true;

  return out;
}
